import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UpdatePartnerRequest } from '../../../../models/requests/update-partner-request.model';
import { buildValidationMessages } from '../../../../web/components/error-message.service';
import { PartnerService } from '../services/partner.service';
import { ProfileService } from '../../../../auth/services/profile.service';
import { AlertNotificationsService } from '../../../../../services/alert-notifications.service';
import { emailValidator, phoneValidator } from '../../../../web/components/validators';
import { AccountService } from '../../../../auth/services/account.service';
import { AuthService } from '../../../../auth/auth.service';
import { DeleteAccountConfirmModalComponent } from './delete-account-confirm-modal.component';
import { LocationService } from '../services/location.service';
import { Country } from '../../../../../model/country.model';
import { Region } from '../../../../../model/region.model';
import { District } from '../../../../../model/district.model';
import { HealthDataDeclaration } from '../../../../models/partner-permission.model';
import { AccountDeletionStatus } from '../../../../models/partner.model';

@Component({
  selector: 'app-settings-modal',
  templateUrl: './settings-modal.component.html',
  styleUrls: ['./settings.component.css'],
  standalone: false,
})
export class SettingsModalComponent implements OnInit {
  updateFields: FormlyFieldConfig[] = [];
  updateForm = new FormGroup({});
  updateModel = {} as UpdatePartnerRequest;

  passwordFields: FormlyFieldConfig[] = [];
  passwordForm = new FormGroup({});
  passwordModel = {} as {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  };

  preferenceFields: FormlyFieldConfig[] = [];
  preferenceForm = new FormGroup({});
  preferenceModel = {} as { holidayMode: boolean };
  originalHolidayMode: boolean | null = null;

  isSubmitting = false;
  pendingEmailNotice?: string;
  currentEmail?: string;
  activeNav = 0;
  countries: Country[] = [];
  regions: Region[] = [];
  districts: District[] = [];
  updateOptions: FormlyFormOptions = {
    formState: {
      validationMessages: buildValidationMessages(),
    },
  };

  constructor(
    private partnerService: PartnerService,
    protected profileService: ProfileService,
    private notificationAlert: AlertNotificationsService,
    private accountService: AccountService,
    private authService: AuthService,
    private locationService: LocationService,
    private modalService: NgbModal,
    public activeModal: NgbActiveModal
  ) {
  }

  async ngOnInit() {
    if (!this.profileService.isOwner) {
      this.activeNav = 1;
    }

    // Load location data BEFORE building the form, so the country/region/district
    // <select> options are already populated when the cascade hooks run.
    if (this.profileService.isOwner) {
      const partner = this.profileService.profile?.partner;
      await this.loadCountries();
      if (partner?.countryId) {
        await this.loadRegions(partner.countryId);
        if (partner.regionId) {
          const selectedRegion = this.regions.find(r => r.id === partner.regionId);
          if (selectedRegion?.districts) {
            this.districts = selectedRegion.districts;
          }
        }
      }
    }

    this.initializeForm();
    this.initializePasswordForm();
    this.initializePreferenceForm();
    this.loadCurrentData(this.activeNav);
  }

  initializeForm() {
    this.updateFields = [
      {
        template:
          '<h6 class="fw-semibold text-muted mt-2 mb-3 pb-2 border-bottom">Company info</h6>',
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-6',
            key: 'companyName',
            type: 'input',
            templateOptions: {
              label: 'Company Name',
              placeholder: 'Enter company name',
              required: true,
              minLength: 2,
              disabled: true,
            },
          },
          {
            className: 'col-md-6',
            key: 'vat',
            type: 'input',
            templateOptions: {
              label: 'Vat Number',
              placeholder: 'Enter Vat number',
              required: true,
              minLength: 8,
              disabled: true,
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-6',
            key: 'name',
            type: 'input',
            templateOptions: {
              label: 'Clinic Name',
              placeholder: 'Enter clinic name',
              required: true,
              minLength: 3,
            },
          },
        ],
      },
      {
        template:
          '<h6 class="fw-semibold text-muted mt-4 mb-3 pb-2 border-bottom">Representative &amp; contact</h6>',
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-6',
            key: 'representativeFirstName',
            type: 'input',
            templateOptions: {
              label: 'Legal Representative First Name',
              placeholder: 'Enter representative name',
              required: true,
              minLength: 2,
            },
          },
          {
            className: 'col-md-6',
            key: 'representativeLastName',
            type: 'input',
            templateOptions: {
              label: 'Legal Representative Last Name',
              placeholder: 'Enter representative last name',
              required: true,
              minLength: 2,
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-6',
            key: 'email',
            type: 'input',
            props: {
              label: 'Email',
              placeholder: 'Enter email',
              required: true,
              type: 'email',
            },
            validators: {
              email: emailValidator,
            },
          },
          {
            className: 'col-md-6',
            key: 'phoneNumber',
            type: 'phone',
            props: {
              label: 'Phone Number',
              placeholder: 'Enter phone number',
              required: true,
            },
            validators: {
              phone: phoneValidator,
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-6',
            key: 'whatsapp',
            type: 'phone',
            templateOptions: {
              label: 'WhatsApp',
              placeholder: 'Enter WhatsApp number',
              maxLength: 15,
            },
            validators: {
              phone: phoneValidator,
            },
          },
        ],
      },
      {
        template:
          '<h6 class="fw-semibold text-muted mt-4 mb-3 pb-2 border-bottom">Location &amp; address</h6>',
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-4',
            key: 'countryId',
            type: 'select',
            props: {
              translate: false,
              label: 'Country',
              required: true,
              options: this.countries,
              valueProp: 'id',
              labelProp: 'name',
            },
            hooks: {
              onInit: (field) => {
                field.form
                  ?.get('countryId')
                  ?.valueChanges.subscribe(async (countryId: string) => {
                  if (countryId) {
                    await this.loadRegions(countryId);
                    const regionField = field.parent?.fieldGroup?.find(f => f.key === 'regionId');
                    if (regionField?.props) {
                      regionField.props['options'] = this.regions;
                    }
                    field.form?.get('regionId')?.setValue(null);
                    field.form?.get('districtId')?.setValue(null);
                    field.options?.detectChanges?.(field);
                  } else {
                    field.form?.get('regionId')?.setValue(null);
                    field.form?.get('districtId')?.setValue(null);
                  }
                });
              },
            },
          },
          {
            className: 'col-md-4',
            key: 'regionId',
            type: 'select',
            props: {
              translate: false,
              label: 'Region',
              required: true,
              options: this.regions,
              valueProp: 'id',
              labelProp: 'name',
            },
            hooks: {
              onInit: (field) => {
                field.form
                  ?.get('regionId')
                  ?.valueChanges.subscribe((regionId: string) => {
                  if (regionId) {
                    const selectedRegion = this.regions.find(r => r.id === regionId);
                    const districtField = field.parent?.fieldGroup?.find(f => f.key === 'districtId');
                    if (selectedRegion?.districts) {
                      this.districts = selectedRegion.districts;
                      if (districtField?.props) {
                        districtField.props['options'] = selectedRegion.districts;
                      }
                    } else {
                      this.districts = [];
                      if (districtField?.props) {
                        districtField.props['options'] = [];
                      }
                    }
                    field.form?.get('districtId')?.setValue(null);
                    field.options?.detectChanges?.(field);
                  }
                });
              },
            },
          },
          {
            className: 'col-md-4',
            key: 'districtId',
            type: 'select',
            props: {
              translate: false,
              label: 'District',
              required: true,
              options: this.districts,
              valueProp: 'id',
              labelProp: 'name',
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-6',
            key: 'city',
            type: 'input',
            templateOptions: {
              label: 'City',
              placeholder: 'Enter city',
              required: true,
              minLength: 2,
            },
          },
          {
            className: 'col-md-6',
            key: 'zipCode',
            type: 'input',
            templateOptions: {
              label: 'ZIP Code',
              placeholder: 'Enter ZIP code',
              required: true,
              minLength: 3,
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-8',
            key: 'address',
            type: 'input',
            templateOptions: {
              label: 'Address',
              placeholder: 'Full address',
              required: true,
              minLength: 5,
            },
          },
          {
            className: 'col-md-4',
            key: 'homeNumber',
            type: 'input',
            templateOptions: {
              label: 'House Number',
              placeholder: 'House number',
            },
          },
        ],
      },
    ];
  }

  initializePasswordForm() {
    this.passwordFields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            key: 'currentPassword',
            type: 'input',
            templateOptions: {
              label: 'Current Password',
              placeholder: 'Enter current password',
              required: true,
              minLength: 8,
              type: 'password',
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-md-6',
            key: 'newPassword',
            type: 'input',
            templateOptions: {
              label: 'New Password',
              placeholder: 'Enter new password',
              required: true,
              minLength: 8,
              type: 'password',
            },
          },
          {
            className: 'col-md-6',
            key: 'confirmPassword',
            type: 'input',
            templateOptions: {
              label: 'Confirm New Password',
              placeholder: 'Confirm new password',
              required: true,
              type: 'password',
            },
            validators: {
              passwordMatch: {
                expression: (control: any) => {
                  const value = control.value;
                  return (
                    !value ||
                    control.parent?.get('newPassword')?.value === value
                  );
                },
                message: 'Passwords do not match',
              },
            },
          },
        ],
      },
    ];
  }

  initializePreferenceForm() {
    this.preferenceFields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            key: 'holidayMode',
            type: 'switch'
          },
        ],
      },
    ];
  }

  async loadCurrentData(tab: number = 0) {
    this.activeNav = tab;
    const partner = this.profileService.profile?.partner;
    if (!partner) return;

    this.updateModel = {
      companyName: partner.companyName || '',
      vat: partner.vat || '',
      name: partner.name || '',
      representativeFirstName: partner.representativeFirstName || '',
      representativeLastName: partner.representativeLastName || '',
      email: partner.email || '',
      phoneNumber: partner.phoneNumber || '',
      whatsapp: partner.whatsapp || '',
      countryId: partner.countryId,
      regionId: partner.regionId,
      districtId: partner.districtId,
      city: partner.city || '',
      zipCode: partner.zipCode || '',
      address: partner.address || '',
      homeNumber: partner.homeNumber || '',
    };
    this.pendingEmailNotice = partner.pendingEmail;
    this.currentEmail = partner.email || '';
    // emitEvent:false so patching country/region doesn't re-trigger the cascade
    // hooks and wipe the saved region/district selections.
    this.updateForm.patchValue(this.updateModel, { emitEvent: false });

    this.preferenceModel = {
      holidayMode: !!partner.holidayModeEnabledAt,
    };
    this.preferenceForm.patchValue(this.preferenceModel);
    if (this.originalHolidayMode === null) {
      this.originalHolidayMode = this.preferenceModel.holidayMode;
    }
  }


  onPartnerTabChange(tab: number) {
    this.activeNav = Number(tab);
    this.loadCurrentData(this.activeNav);
  }

  // async loadCountries() {
  //   try {
  //     this.countries = await this.locationService.getCountries();
  //   } catch (error) {
  //     console.error('Error loading countries:', error);
  //   }
  // }

  async loadCountries() {
    try {
      await this.locationService.getCountries().then((countries: Country[]) => {
        this.countries = [];
        for (const country of countries) {
          if (country.code == 'AL') {
            this.countries.push(country);
          }
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  async loadRegions(countryId: string) {
    try {
      this.regions = await this.locationService.getRegions(countryId);
      this.districts = [];
    } catch (error) {
      console.error('Error loading regions:', error);
      this.regions = [];
      this.districts = [];
    }
  }

  async submit() {
    if (this.updateForm.valid) {
      this.isSubmitting = true;
      try {
        const updateRequest: UpdatePartnerRequest = { ...this.updateForm.value };
        const emailChanged = !!updateRequest.email
          && updateRequest.email.trim().toLowerCase() !== (this.currentEmail || '').trim().toLowerCase();
        const updated = await this.partnerService.updatePartner(updateRequest);
        if (emailChanged && updated?.pendingEmail) {
          this.notificationAlert.success(
            `We've sent a confirmation link to ${updated.pendingEmail}. Your email will change once you confirm.`
          );
        } else {
          this.notificationAlert.success('Partner updated successfully');
        }
        this.activeModal.close('updated');
      } catch (error) {
        console.error('Error updating partner:', error);
      } finally {
        this.isSubmitting = false;
        await this.profileService.getProfile();
        if (this.profileService.profile?.partner?.status != 'APPROVED') {
          window.location.reload();
        }
      }
    }
  }

  async submitPassword() {
    if (this.passwordForm.valid) {
      this.isSubmitting = true;
      try {
        await this.profileService.updatePassword(
          this.passwordModel.currentPassword,
          this.passwordModel.newPassword,
          this.passwordModel.confirmPassword
        );
        this.notificationAlert.success('Password updated successfully');
        this.passwordForm.reset();
        this.passwordModel = {} as any;
      } catch (error) {
        console.error('Error updating password:', error);
      } finally {
        this.isSubmitting = false;
        this.profileService.getProfile();
      }
    }
  }

  async submitPreferences() {
    if (!this.hasHolidayModeChanged()) {
      return;
    }
    this.isSubmitting = true;
    try {
      await this.accountService.toggleHolidayMode(this.preferenceModel.holidayMode);
      await this.profileService.getProfile();
      this.originalHolidayMode = this.preferenceModel.holidayMode;
      this.notificationAlert.success(
        'Preferences updated successfully'
      );
    } catch (error) {
      console.error('Error updating preferences:', error);
    } finally {
      this.isSubmitting = false;
    }
  }

  hasHolidayModeChanged(): boolean {
    if (this.originalHolidayMode === null) return false;
    return this.preferenceModel.holidayMode !== this.originalHolidayMode;
  }

  async changeHealthDeclaration(value: HealthDataDeclaration) {
    this.isSubmitting = true;
    try {
      await this.profileService.updateHealthDeclaration(value);
      this.notificationAlert.success('Health data declaration updated');
      // Lists and details already loaded were masked/unmasked with the old declaration.
      window.location.reload();
    } catch (error) {
      console.error('Error updating health data declaration:', error);
    } finally {
      this.isSubmitting = false;
    }
  }

  cancel() {
    this.activeModal.dismiss('cancel');
  }

  // --- Delete account tab (owner only) ---------------------------------------------------
  deletionStatus: AccountDeletionStatus | null = null;
  isLoadingDeletionStatus = false;
  isRequestingDeletion = false;
  private router = inject(Router);

  get deletionGraceDays(): number {
    return this.deletionStatus?.graceDays ?? 90;
  }

  /** Pre-check (balance, payment in progress) shown before enabling the delete button. */
  async loadDeletionStatus() {
    if (!this.profileService.isOwner || this.isLoadingDeletionStatus) {
      return;
    }
    this.isLoadingDeletionStatus = true;
    try {
      this.deletionStatus = await this.accountService.getAccountDeletionStatus();
    } catch (error) {
      console.error('Error loading account deletion status:', error);
      this.deletionStatus = null;
    } finally {
      this.isLoadingDeletionStatus = false;
    }
  }

  async openDeleteAccountConfirm() {
    if (!this.profileService.isPartner) {
      return;
    }

    const modalRef = this.modalService.open(DeleteAccountConfirmModalComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.graceDays = this.deletionGraceDays;
    let result: any;
    try {
      result = await modalRef.result;
    } catch {
      return; // dismissed — do nothing
    }
    if (result !== 'confirm') {
      return;
    }

    this.isRequestingDeletion = true;
    try {
      await this.accountService.deleteAccount();
      // The owner stays signed in: the refreshed profile (PENDING_DELETION) locks the panel on the
      // "deletion scheduled" page, where the deletion can be cancelled.
      await this.profileService.getProfile();
      this.activeModal.dismiss('deleted');
      this.notificationAlert.success('Your account deletion has been scheduled.');
      await this.router.navigate(['/partners/deletion-scheduled']);
    } catch (error: any) {
      // e.g. outstanding balance (422): the interceptor already shows the backend message; refresh the pre-check.
      console.error('Error requesting account deletion:', error);
      await this.loadDeletionStatus();
    } finally {
      this.isRequestingDeletion = false;
    }
  }
}
